import { useRef } from "react";
import { useFormikContext } from "formik";
import isEqual from "lodash/isEqual";
import { useAutoSave } from "newui/hooks/useAutoSave";

interface FormikAutoSaveProps {
	/** Debounce interval in milliseconds (default: 3000) */
	debounceMs?: number;
	/** External error flag — suppresses auto-save when true */
	hasError?: boolean;
}

/**
 * Drop-in Formik auto-save component.
 *
 * Place inside a `<Formik>` render tree. Automatically submits the form
 * after `debounceMs` of inactivity when the form is dirty, valid, and
 * the values have changed since the last successful save.
 *
 * Usage:
 * ```tsx
 * <Formik ...>
 *   <Form>
 *     <FormikAutoSave debounceMs={3000} />
 *     ...fields...
 *   </Form>
 * </Formik>
 * ```
 */
export const FormikAutoSave = <T extends Record<string, any>>({
	debounceMs = 3000,
	hasError = false,
}: FormikAutoSaveProps) => {
	const { dirty, isSubmitting, submitForm, validateForm, values } =
		useFormikContext<T>();

	const savedValuesRef = useRef<T>(values);

	const isDirty =
		dirty &&
		!hasError &&
		!isSubmitting &&
		!isEqual(savedValuesRef.current, values);

	useAutoSave({
		isDirty,
		onSave: async () => {
			const validationErrors = await validateForm();
			if (Object.keys(validationErrors).length > 0) {
				return;
			}
			await submitForm();
			savedValuesRef.current = values;
		},
		debounceMs,
	});

	return null;
};
